import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { adminBroadcastsService } from '@/services/admin-broadcasts.service';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import type { EmailTemplateView, SubscriberListView } from '@/types/api';

export function AdminCreateBroadcastPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplateView[]>([]);
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [listId, setListId] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([adminEmailTemplatesService.list(), adminSubscriberListsService.list()])
      .then(([t, l]) => {
        setTemplates(t);
        setLists(l);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Could not load data.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!templateId || !listId || creating) return;
    setCreating(true);
    try {
      const draft = await adminBroadcastsService.create({ templateId, listId });
      navigate(`/admin/subscribers/broadcasts/${draft.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create broadcast.');
      setCreating(false);
    }
  };

  const selectedList = lists.find((l) => l.id === listId);

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/subscribers/broadcasts')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
            aria-label="Back to broadcasts"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div>
            <div className="or-kicker text-or-orange-600">New broadcast</div>
            <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Create a broadcast
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-or-slate-500">Loading…</div>
        ) : (
          <div className="flex max-w-lg flex-col gap-5 rounded-2xl border border-or-slate-200 bg-white p-6">
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
              Template
              {templates.length === 0 ? (
                <p className="text-[12px] font-normal normal-case text-or-slate-500">
                  No templates yet —{' '}
                  <Link to="/admin/subscribers/templates/new" className="text-or-orange-600 underline">
                    create one first
                  </Link>
                  .
                </p>
              ) : (
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                >
                  <option value="">Choose a template…</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.format})
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
              List
              <select
                value={listId}
                onChange={(e) => setListId(e.target.value)}
                className="h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              >
                <option value="">Choose a list…</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.memberCount} members)
                  </option>
                ))}
              </select>
            </label>

            {selectedList ? (
              <p className="text-[12px] text-or-slate-500">
                Up to {selectedList.memberCount.toLocaleString()} active member(s) will be eligible —
                you'll preview and confirm before anything sends.
              </p>
            ) : null}

            <div className="flex justify-end">
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                disabled={!templateId || !listId || creating}
              >
                {creating ? 'Creating…' : 'Create draft'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
